import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { qaReportsApi } from "../../../viewmodels/qaReports/qaReportsApi";
import { ALL_PRODUCTS } from "../../../viewmodels/qaReports/qaReportConfig";
import type { ProductOption } from "../../../viewmodels/qaReports/qaReportTypes";

type Props = {
  value: ProductOption | null;
  onChange: (value: ProductOption | null) => void;
  allowAll?: boolean;
  disabled?: boolean;
  logout: () => void;
};


export default function ProductPluSearch({ value, onChange, allowAll = false, disabled = false, logout }: Props) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    let active = true;
    if (!open) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(async () => {
      try {
        const result = await qaReportsApi.products(query, page);
        // Ignore late results after typing or unmounting.
        if (!active) return;
        if (!result.success) {
          if (result.errorStatus === 401) logoutRef.current();
          throw new Error("Product search failed");
        }
        const matches = result.data.data.map((product): ProductOption => ({
          value: product.id,
          label: `${product.plu} — ${product.name}`,
          plu: product.plu,
          name: product.name,
        }));
        setOptions((current) => page === 1 ? matches : [...current, ...matches]);
        setHasMore(result.data.has_more);
      } catch {
        if (active) setError("Could not search products. Check your connection and retry.");
      } finally {
        if (active) setLoading(false);
      }
    }, page === 1 && query.trim() ? 300 : 0);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, page, retry, open]);

  return (
    <div className="col-span-6 sm:col-span-3 lg:col-span-2">
      <label htmlFor="qa-product-search" className="mb-1 block text-sm font-medium text-gray-900">
        Product / PLU Search
      </label>
      <Select<ProductOption>
        inputId="qa-product-search"
        name="product"
        value={value?.value === -1 && !allowAll ? null : value}
        options={allowAll ? [ALL_PRODUCTS, ...options] : options}
        inputValue={open ? query : ""}
        filterOption={null}
        isDisabled={disabled}
        isClearable
        isLoading={loading}
        placeholder="Type a PLU or product name"
        menuIsOpen={open}
        onMenuOpen={() => {
          setPage(1);
          setOptions([]);
          setHasMore(false);
          setOpen(true);
        }}
        tabSelectsValue={false}
        onMenuClose={() => { setOpen(false); setQuery(""); }}
        onInputChange={(input, action) => {
          if (action.action !== "input-change") return;
          setQuery(input);
          setPage(1);
          setOptions([]);
          setHasMore(false);
          setError(null);
        }}
        onChange={(product) => {
          setQuery("");
          setOptions([]);
          setPage(1);
          onChange(product ?? (allowAll ? ALL_PRODUCTS : null));
        }}
        captureMenuScroll
        onMenuScrollToBottom={() => {
          if (hasMore && !loading && !error) setPage((current) => current + 1);
        }}
        noOptionsMessage={() => loading ? "Searching…" : error ? "Search unavailable" : "No matching products"}
        styles={{ control: (base) => ({ ...base, minHeight: 36, fontSize: 15 }) }}
        aria-invalid={Boolean(error)}
        aria-errormessage={error ? "qa-product-search-error" : undefined}
        aria-describedby={error ? "qa-product-search-error" : loading ? "qa-product-search-help" : undefined}
      />
      {error ? (
        <div id="qa-product-search-error" role="alert" className="mt-1 text-sm text-red-600">
          {error}{" "}
          <button type="button" className="underline" onClick={() => { setOpen(true); setRetry((current) => current + 1); }}>
            Retry search
          </button>
        </div>
      ) : loading ? (
        <p id="qa-product-search-help" role="status" className="mt-1 text-xs text-gray-600">
          Searching products…
        </p>
      ) : null}
    </div>
  );
}
